import React from 'react';
import { useSaaSContext } from '../../store/SaaSContext';
import { 
  Scissors, 
  Target, 
  Layers, 
  Sparkles, 
  QrCode, 
  Crosshair, 
  Square,
  Focus,
  Disc
} from 'lucide-react';
import { Unit } from '../../types';
import { formatUnit } from '../../utils/units';

interface PlotterFinishingPanelProps {
  onClose?: () => void;
}

export const PlotterFinishingPanel: React.FC<PlotterFinishingPanelProps> = ({ onClose }) => {
  const { layout, setLayout, appUnit } = useSaaSContext();

  const handleUpdate = (field: string, value: any) => {
    setLayout({
      ...layout,
      [field]: value
    });
  };

  const plotterType = layout.plotterMarkType || 'arms';
  const markOffsetPt = layout.plotterMarkOffset !== undefined ? layout.plotterMarkOffset : 15;
  const markSizePt = layout.plotterMarkSize !== undefined ? layout.plotterMarkSize : 12;

  const markOffsetDisplay = formatUnit(markOffsetPt, appUnit as Unit);
  const markSizeDisplay = formatUnit(markSizePt, appUnit as Unit);

  const cutContourName = layout.cutContourName || 'CutContour';
  const cutContourColor = layout.cutContourColor || '#FF00FF';
  const cutContourStroke = layout.cutContourStrokeWidth !== undefined ? layout.cutContourStrokeWidth : 0.25;

  const kissCutInsetPt = layout.kissCutInset !== undefined ? layout.kissCutInset : 2;
  const kissCutInsetDisplay = formatUnit(kissCutInsetPt, appUnit as Unit);
  const kissCutColor = layout.kissCutColor || '#FF00FF';
  const thruCutColor = layout.thruCutColor || '#00FFFF';

  return (
    <div className="flex flex-col gap-4 text-zinc-200">
      
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-pink-950/80 via-zinc-900 to-indigo-950/80 p-3.5 rounded-xl border border-pink-500/30 flex items-center justify-between gap-3 shadow-md">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-pink-500/20 text-pink-400 border border-pink-500/30 flex items-center justify-center shrink-0">
            <Scissors className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-sm text-white flex items-center gap-1.5">
              Cutting Plotter &amp; Die-Cut Finishing
              <span className="text-[10px] bg-pink-500/20 text-pink-300 font-mono px-2 py-0.5 rounded-full border border-pink-500/30">
                Phase 1
              </span>
            </h3>
            <p className="text-xs text-zinc-400">
              Optical alignment marks (ARMS/OPOS) and RIP CutContour spot layers for Roland, Graphtec, Summa &amp; Mimaki.
            </p>
          </div>
        </div>
      </div>

      {/* SECTION 1: PLOTTER REGISTRATION MARKS */}
      <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-pink-400" />
            <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-100">
              1. Optical Registration Marks (ARMS / OPOS)
            </h4>
          </div>
          <label className="relative inline-flex items-center cursor-pointer select-none">
            <input 
              type="checkbox"
              className="sr-only peer"
              checked={Boolean(layout.plotterMarks)}
              onChange={(e) => handleUpdate('plotterMarks', e.target.checked)}
            />
            <div className="w-9 h-5 bg-zinc-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-zinc-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-pink-600"></div>
          </label>
        </div>

        {layout.plotterMarks && (
          <div className="space-y-4 pt-2 border-t border-zinc-800/80 animate-in fade-in duration-200">
            
            {/* Plotter System Presets */}
            <div className="space-y-2">
              <label className="text-xs font-semibold text-zinc-300 flex items-center justify-between">
                <span>Select Plotter System &amp; Target Mark Type</span>
                <span className="text-[10px] text-zinc-500">Auto-Detect RIP Compatibility</span>
              </label>

              <div className="grid grid-cols-2 gap-2">
                {/* ARMS / Graphtec */}
                <button
                  type="button"
                  onClick={() => handleUpdate('plotterMarkType', 'arms')}
                  className={`p-2.5 rounded-lg border text-left flex items-start gap-2.5 transition-all cursor-pointer ${
                    plotterType === 'arms' || plotterType === 'graphtec'
                      ? 'bg-pink-950/40 border-pink-500 text-white shadow-sm'
                      : 'bg-zinc-900/60 border-zinc-800 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200'
                  }`}
                >
                  <Crosshair className={`w-4 h-4 mt-0.5 shrink-0 ${plotterType === 'arms' ? 'text-pink-400' : 'text-zinc-500'}`} />
                  <div>
                    <div className="text-xs font-bold text-zinc-200">ARMS / Graphtec</div>
                    <div className="text-[10px] text-zinc-400">4-Corner Circle Alignment Dots (Graphtec, Silhouette)</div>
                  </div>
                </button>

                {/* OPOS / Summa */}
                <button
                  type="button"
                  onClick={() => handleUpdate('plotterMarkType', 'opos')}
                  className={`p-2.5 rounded-lg border text-left flex items-start gap-2.5 transition-all cursor-pointer ${
                    plotterType === 'opos'
                      ? 'bg-pink-950/40 border-pink-500 text-white shadow-sm'
                      : 'bg-zinc-900/60 border-zinc-800 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200'
                  }`}
                >
                  <Square className={`w-4 h-4 mt-0.5 shrink-0 ${plotterType === 'opos' ? 'text-pink-400' : 'text-zinc-500'}`} />
                  <div>
                    <div className="text-xs font-bold text-zinc-200">OPOS / Summa</div>
                    <div className="text-[10px] text-zinc-400">L-Bracket Corner Targets (SummaCut, S-Class)</div>
                  </div>
                </button>

                {/* Roland Contour */}
                <button
                  type="button"
                  onClick={() => handleUpdate('plotterMarkType', 'roland')}
                  className={`p-2.5 rounded-lg border text-left flex items-start gap-2.5 transition-all cursor-pointer ${
                    plotterType === 'roland'
                      ? 'bg-pink-950/40 border-pink-500 text-white shadow-sm'
                      : 'bg-zinc-900/60 border-zinc-800 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200'
                  }`}
                >
                  <Target className={`w-4 h-4 mt-0.5 shrink-0 ${plotterType === 'roland' ? 'text-pink-400' : 'text-zinc-500'}`} />
                  <div>
                    <div className="text-xs font-bold text-zinc-200">Roland Target</div>
                    <div className="text-[10px] text-zinc-400">Concentric Circles + Center Cross (VersaCAMM, TrueVIS)</div>
                  </div>
                </button>

                {/* Barcode & QR Target */}
                <button
                  type="button"
                  onClick={() => handleUpdate('plotterMarkType', 'barcode_target')}
                  className={`p-2.5 rounded-lg border text-left flex items-start gap-2.5 transition-all cursor-pointer ${
                    plotterType === 'barcode_target'
                      ? 'bg-pink-950/40 border-pink-500 text-white shadow-sm'
                      : 'bg-zinc-900/60 border-zinc-800 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200'
                  }`}
                >
                  <QrCode className={`w-4 h-4 mt-0.5 shrink-0 ${plotterType === 'barcode_target' ? 'text-pink-400' : 'text-zinc-500'}`} />
                  <div>
                    <div className="text-xs font-bold text-zinc-200">Auto Barcode Target</div>
                    <div className="text-[10px] text-zinc-400">Optical Target + Job Identification Barcode for Auto-Cutters</div>
                  </div>
                </button>

                {/* Mimaki Target */}
                <button
                  type="button"
                  onClick={() => handleUpdate('plotterMarkType', 'mimaki')}
                  className={`p-2.5 rounded-lg border text-left flex items-start gap-2.5 transition-all cursor-pointer ${
                    plotterType === 'mimaki'
                      ? 'bg-pink-950/40 border-pink-500 text-white shadow-sm'
                      : 'bg-zinc-900/60 border-zinc-800 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200'
                  }`}
                >
                  <Focus className={`w-4 h-4 mt-0.5 shrink-0 ${plotterType === 'mimaki' ? 'text-pink-400' : 'text-zinc-500'}`} />
                  <div>
                    <div className="text-xs font-bold text-zinc-200">Mimaki Target</div>
                    <div className="text-[10px] text-zinc-400">Inverted-T &amp; Corner Bracket (CG-FX, CG-SR Series)</div>
                  </div>
                </button>

                {/* Mutoh Target */}
                <button
                  type="button"
                  onClick={() => handleUpdate('plotterMarkType', 'mutoh')}
                  className={`p-2.5 rounded-lg border text-left flex items-start gap-2.5 transition-all cursor-pointer ${
                    plotterType === 'mutoh'
                      ? 'bg-pink-950/40 border-pink-500 text-white shadow-sm'
                      : 'bg-zinc-900/60 border-zinc-800 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200'
                  }`}
                >
                  <Disc className={`w-4 h-4 mt-0.5 shrink-0 ${plotterType === 'mutoh' ? 'text-pink-400' : 'text-zinc-500'}`} />
                  <div>
                    <div className="text-xs font-bold text-zinc-200">Mutoh Target</div>
                    <div className="text-[10px] text-zinc-400">Dual-Axis Quiet-Ring Crosshair (ValueCut Series)</div>
                  </div>
                </button>
              </div>
            </div>

            {/* Sliders: Safe Sensor Margin Offset & Target Size */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-zinc-900/70 p-3 rounded-lg border border-zinc-800">
              
              {/* Sensor Safe Margin Offset */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium text-zinc-300">Sensor Margin Offset</span>
                  <span className="font-mono font-bold text-pink-400">{markOffsetDisplay} {appUnit}</span>
                </div>
                <input 
                  type="range"
                  min="5"
                  max="50"
                  step="1"
                  value={markOffsetPt}
                  onChange={(e) => handleUpdate('plotterMarkOffset', Number(e.target.value))}
                  className="w-full accent-pink-500 cursor-pointer h-1.5 bg-zinc-800 rounded-lg"
                />
                <p className="text-[10px] text-zinc-500">Distance from sheet boundary to plotter marks for optical readability.</p>
              </div>

              {/* Target Mark Size */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium text-zinc-300">Target Mark Size</span>
                  <span className="font-mono font-bold text-pink-400">{markSizeDisplay} {appUnit}</span>
                </div>
                <input 
                  type="range"
                  min="6"
                  max="30"
                  step="1"
                  value={markSizePt}
                  onChange={(e) => handleUpdate('plotterMarkSize', Number(e.target.value))}
                  className="w-full accent-pink-500 cursor-pointer h-1.5 bg-zinc-800 rounded-lg"
                />
                <p className="text-[10px] text-zinc-500">Diameter or length of optical targets.</p>
              </div>

            </div>

          </div>
        )}
      </div>

      {/* SECTION 2: DEDICATED CUTCONTOUR SPOT LAYER EXPORT */}
      <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-purple-400" />
            <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-100">
              2. Dedicated CutContour Spot Layer (RIP Ready)
            </h4>
          </div>
          <label className="relative inline-flex items-center cursor-pointer select-none">
            <input 
              type="checkbox"
              className="sr-only peer"
              checked={Boolean(layout.enableCutContour)}
              onChange={(e) => handleUpdate('enableCutContour', e.target.checked)}
            />
            <div className="w-9 h-5 bg-zinc-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-zinc-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-purple-600"></div>
          </label>
        </div>

        {layout.enableCutContour && (
          <div className="space-y-4 pt-2 border-t border-zinc-800/80 animate-in fade-in duration-200">
            
            {/* Spot Layer Name Presets */}
            <div className="space-y-2">
              <label className="text-xs font-semibold text-zinc-300 flex items-center justify-between">
                <span>RIP Spot Color Layer Name</span>
                <span className="text-[10px] text-zinc-500">RIP Software Spec</span>
              </label>

              <div className="flex flex-wrap items-center gap-2">
                {['CutContour', 'PerfCutContour', 'KissCut', 'ThruCut'].map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => handleUpdate('cutContourName', name)}
                    className={`px-3 py-1.5 rounded-md text-xs font-mono font-bold border transition-all cursor-pointer ${
                      cutContourName === name
                        ? 'bg-purple-950 text-purple-300 border-purple-500 shadow-xs'
                        : 'bg-zinc-900 text-zinc-400 border-zinc-800 hover:bg-zinc-800 hover:text-zinc-200'
                    }`}
                  >
                    {name}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2 mt-2">
                <span className="text-xs text-zinc-400">Custom Name:</span>
                <input 
                  type="text"
                  value={cutContourName}
                  onChange={(e) => handleUpdate('cutContourName', e.target.value)}
                  placeholder="e.g. CutContour"
                  className="bg-zinc-900 border border-zinc-700 text-zinc-200 text-xs font-mono font-bold px-2.5 py-1 rounded-md outline-none focus:border-purple-500 flex-1"
                />
              </div>
            </div>

            {/* Stroke Color & Weight */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-zinc-900/70 p-3 rounded-lg border border-zinc-800">
              
              {/* Color Picker */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-zinc-300">Spot Color Identifier</label>
                <div className="flex items-center gap-2">
                  <input 
                    type="color"
                    value={cutContourColor}
                    onChange={(e) => handleUpdate('cutContourColor', e.target.value)}
                    className="w-8 h-8 rounded border border-zinc-700 cursor-pointer bg-transparent"
                  />
                  <input 
                    type="text"
                    value={cutContourColor}
                    onChange={(e) => handleUpdate('cutContourColor', e.target.value)}
                    className="bg-zinc-900 border border-zinc-700 text-zinc-200 text-xs font-mono px-2.5 py-1.5 rounded-md outline-none focus:border-purple-500 flex-1 uppercase"
                  />
                </div>
              </div>

              {/* Stroke Weight */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-zinc-300">Line Stroke Weight</label>
                <div className="flex items-center gap-1.5">
                  {[0.25, 0.5, 1.0].map((sw) => (
                    <button
                      key={sw}
                      type="button"
                      onClick={() => handleUpdate('cutContourStrokeWidth', sw)}
                      className={`flex-1 py-1.5 rounded-md text-xs font-mono font-bold border transition-all cursor-pointer ${
                        cutContourStroke === sw
                          ? 'bg-purple-950 text-purple-300 border-purple-500'
                          : 'bg-zinc-900 text-zinc-400 border-zinc-800 hover:bg-zinc-800'
                      }`}
                    >
                      {sw === 0.25 ? '0.25pt (Hairline)' : `${sw}pt`}
                    </button>
                  ))}
                </div>
              </div>

            </div>

          </div>
        )}
      </div>

      {/* SECTION 3: DUAL CUT LINE SUPPORT (KISS-CUT + DIE-CUT THRU) */}
      <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-emerald-400" />
            <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-100">
              3. Dual Cut Lines (Kiss-Cut + Die-Cut Thru)
            </h4>
          </div>
          <label className="relative inline-flex items-center cursor-pointer select-none">
            <input 
              type="checkbox"
              className="sr-only peer"
              checked={Boolean(layout.enableDualCutLine)}
              onChange={(e) => handleUpdate('enableDualCutLine', e.target.checked)}
            />
            <div className="w-9 h-5 bg-zinc-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-zinc-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-600"></div>
          </label>
        </div>

        {layout.enableDualCutLine && (
          <div className="space-y-4 pt-2 border-t border-zinc-800/80 animate-in fade-in duration-200">
            <p className="text-xs text-zinc-400">
              Generate two distinct vector cutlines per sticker: an inner <strong className="text-pink-400">Kiss-Cut</strong> line for easy peeling and an outer <strong className="text-emerald-400">Thru-Cut (Die-Cut)</strong> line for full punch-out.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-zinc-900/70 p-3 rounded-lg border border-zinc-800">
              
              {/* Kiss-Cut Inset Slider */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium text-zinc-300">Kiss-Cut Inset Distance</span>
                  <span className="font-mono font-bold text-pink-400">-{kissCutInsetDisplay} {appUnit}</span>
                </div>
                <input 
                  type="range"
                  min="0.5"
                  max="10"
                  step="0.5"
                  value={kissCutInsetPt}
                  onChange={(e) => handleUpdate('kissCutInset', Number(e.target.value))}
                  className="w-full accent-pink-500 cursor-pointer h-1.5 bg-zinc-800 rounded-lg"
                />
                <p className="text-[10px] text-zinc-500">Inset inside sticker edge for peelable boundary.</p>
              </div>

              {/* Dual Colors */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-pink-400 font-bold">Kiss-Cut (Peel):</span>
                  <input 
                    type="color" 
                    value={kissCutColor} 
                    onChange={(e) => handleUpdate('kissCutColor', e.target.value)}
                    className="w-6 h-6 rounded border border-zinc-700 cursor-pointer bg-transparent"
                  />
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-emerald-400 font-bold">Thru-Cut (Die Punch):</span>
                  <input 
                    type="color" 
                    value={thruCutColor} 
                    onChange={(e) => handleUpdate('thruCutColor', e.target.value)}
                    className="w-6 h-6 rounded border border-zinc-700 cursor-pointer bg-transparent"
                  />
                </div>
              </div>

            </div>

          </div>
        )}
      </div>

      {/* Footer Action Buttons */}
      <div className="pt-2 border-t border-zinc-800 flex items-center justify-between gap-2">
        <span className="text-[10px] text-zinc-500 font-mono">Plotter / ARMS Engine Active</span>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="px-3.5 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-lg shadow-sm transition-colors cursor-pointer"
          >
            Apply & Close
          </button>
        )}
      </div>
    </div>
  );
};
